/**
 * /reports/asset-rollforward — the schedule auditors actually ask for.
 *
 * Per asset class: beginning cost / accumulated / NBV → additions_cost →
 * depreciation → disposals_cost → ending. Also hosts the register↔GL tie-out
 * section (per accumulated-depreciation account: register accumulated vs
 * posted GL balance, with the delta) — this is the link target from the
 * /assets tie-out banner. See MONEY-TAX-DEPRECIATION-001-spec.md §5.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/reports/asset-rollforward")({
  component: AssetRollforwardReportPage,
});

interface RollforwardRow {
  asset_class: string;
  beginning_cost: number;
  beginning_accum_depr: number;
  beginning_nbv: number;
  additions_cost: number;
  depreciation: number;
  disposals_cost: number;
  disposals_accum_depr: number;
  ending_cost: number;
  ending_accum_depr: number;
  ending_nbv: number;
}

interface AssetRollforwardReport {
  year: number;
  groups: RollforwardRow[];
}

interface TieoutAccountRow {
  accum_depr_account_id: string;
  account_code: string;
  account_name: string;
  register_accumulated: number;
  gl_balance: number;
  delta: number;
  is_balanced: boolean;
}

interface AssetGlTieoutResponse {
  rows: TieoutAccountRow[];
  all_balanced: boolean;
}

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 1990;
const MAX_YEAR = 2099;

const assetRollforwardReportQueryOptions = (year: number) =>
  queryOptions({
    queryKey: ["reports", "asset-rollforward", year],
    queryFn: () => api<AssetRollforwardReport>(`/api/reports/asset-rollforward?year=${year}`),
  });

const assetGlTieoutQueryOptions = () =>
  queryOptions({
    queryKey: ["asset-gl-tieout"],
    queryFn: () => api<AssetGlTieoutResponse>("/api/reports/asset-gl-tieout"),
  });

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

const ZERO_ROW: RollforwardRow = {
  asset_class: "Total", beginning_cost: 0, beginning_accum_depr: 0, beginning_nbv: 0,
  additions_cost: 0, depreciation: 0, disposals_cost: 0, disposals_accum_depr: 0,
  ending_cost: 0, ending_accum_depr: 0, ending_nbv: 0,
};

function sumRows(rows: RollforwardRow[]): RollforwardRow {
  return rows.reduce((acc, r) => ({
    asset_class: "Total",
    beginning_cost: acc.beginning_cost + Number(r.beginning_cost ?? 0),
    beginning_accum_depr: acc.beginning_accum_depr + Number(r.beginning_accum_depr ?? 0),
    beginning_nbv: acc.beginning_nbv + Number(r.beginning_nbv ?? 0),
    additions_cost: acc.additions_cost + Number(r.additions_cost ?? 0),
    depreciation: acc.depreciation + Number(r.depreciation ?? 0),
    disposals_cost: acc.disposals_cost + Number(r.disposals_cost ?? 0),
    disposals_accum_depr: acc.disposals_accum_depr + Number(r.disposals_accum_depr ?? 0),
    ending_cost: acc.ending_cost + Number(r.ending_cost ?? 0),
    ending_accum_depr: acc.ending_accum_depr + Number(r.ending_accum_depr ?? 0),
    ending_nbv: acc.ending_nbv + Number(r.ending_nbv ?? 0),
  }), { ...ZERO_ROW });
}

function AssetRollforwardReportPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const reportQ = useQuery(assetRollforwardReportQueryOptions(year));
  const tieoutQ = useQuery(assetGlTieoutQueryOptions());

  const rows = reportQ.data?.groups ?? [];
  const totals = sumRows(rows);  // backend sends groups only; totals are client-derived
  const tieoutRows = tieoutQ.data?.rows ?? [];

  function exportCsv() {
    const header = [
      "asset_class", "beginning_cost", "beginning_accum_depr", "beginning_nbv",
      "additions_cost", "depreciation", "disposals_cost", "disposals_accum_depr",
      "ending_cost", "ending_accum_depr", "ending_nbv",
    ];
    const lines = [header.join(",")];
    for (const r of rows) {
      const cells = [
        r.asset_class, r.beginning_cost, r.beginning_accum_depr, r.beginning_nbv,
        r.additions_cost, r.depreciation, r.disposals_cost, r.disposals_accum_depr,
        r.ending_cost, r.ending_accum_depr, r.ending_nbv,
      ].map((c) => {
        const s = String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      });
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asset-rollforward-${year}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoGraphIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Asset Rollforward</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <TextField
          label="Year" type="number" size="small" sx={{ minWidth: 140 }}
          inputProps={{ min: MIN_YEAR, max: MAX_YEAR }}
          value={year}
          onChange={(e) => {
            // Skip the transient empty string while the user is retyping —
            // only commit once it parses, so backspacing doesn't snap back.
            if (e.target.value === "") return;
            const n = Number(e.target.value);
            if (Number.isFinite(n)) setYear(n);
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<DownloadIcon />} onClick={exportCsv} disabled={rows.length === 0}>
          Export CSV
        </Button>
      </Stack>

      {reportQ.isLoading && <CircularProgress size={20} />}
      {reportQ.error && <Alert severity="error">Failed to load report.</Alert>}
      {!reportQ.isLoading && !reportQ.error && rows.length === 0 && (
        <Alert severity="info">No asset activity for {year}.</Alert>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Asset Class</TableCell>
                <TableCell align="right">Beg. Cost</TableCell>
                <TableCell align="right">Beg. Accum.</TableCell>
                <TableCell align="right">Beg. NBV</TableCell>
                <TableCell align="right">Additions</TableCell>
                <TableCell align="right">Depreciation</TableCell>
                <TableCell align="right">Disposals — Cost</TableCell>
                <TableCell align="right">Disposals — Accum. Depr.</TableCell>
                <TableCell align="right">End Cost</TableCell>
                <TableCell align="right">End Accum.</TableCell>
                <TableCell align="right">End NBV</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.asset_class} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{r.asset_class || "(no class)"}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.beginning_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.beginning_accum_depr)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.beginning_nbv)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.additions_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.depreciation)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.disposals_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.disposals_accum_depr)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.ending_cost)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.ending_accum_depr)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>{fmtMoney(r.ending_nbv)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { fontWeight: 700, borderTop: 2, borderColor: "divider" } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.beginning_cost)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.beginning_accum_depr)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.beginning_nbv)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.additions_cost)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.depreciation)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.disposals_cost)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.disposals_accum_depr)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.ending_cost)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.ending_accum_depr)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.ending_nbv)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Register ↔ GL tie-out — per accumulated-depreciation account. */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Register / GL Tie-Out</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Σ register accumulated depreciation vs the posted GL account balance, per account.
      </Typography>

      {tieoutQ.isLoading && <CircularProgress size={20} />}
      {tieoutQ.error && <Alert severity="error">Failed to load tie-out.</Alert>}
      {!tieoutQ.isLoading && !tieoutQ.error && tieoutRows.length === 0 && (
        <Alert severity="info">No accumulated-depreciation accounts to tie out.</Alert>
      )}

      {tieoutRows.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Account</TableCell>
                <TableCell align="right">Register Accumulated</TableCell>
                <TableCell align="right">GL Balance</TableCell>
                <TableCell align="right">Delta</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tieoutRows.map((r) => (
                <TableRow key={r.accum_depr_account_id} hover>
                  <TableCell>{r.account_code} · {r.account_name}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.register_accumulated)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.gl_balance)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", color: r.is_balanced ? "text.primary" : "error.main" }}>
                    {fmtMoney(r.delta)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={r.is_balanced ? "Balanced" : "Out of balance"}
                      color={r.is_balanced ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
